/**
 * Reading and writing the coalesced tick frame.
 *
 * ONE definition, used by both ends. The engine packs with `FrameWriter` and
 * the browser unpacks with `readFrame`, and they live in the same file because
 * a disagreement about byte offsets between two copies of this logic is not a
 * crash — it is a plausible-looking wrong price.
 *
 * Layout is documented in `feed.ts`. Little-endian throughout.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * BOTH DIRECTIONS ARE ALLOCATION-FREE BY DESIGN
 *
 * The writer owns one buffer for the life of the process and overwrites it
 * every flush. The reader never builds an object per record — it hands the
 * caller primitives and lets the caller write them straight into its own
 * typed arrays.
 *
 * That is why `readFrame` takes a callback rather than returning an array of
 * records: returning records would allocate one object per changed instrument
 * per flush, which at 12 Hz across a chain is exactly the garbage the whole
 * typed-array design exists to avoid.
 * ─────────────────────────────────────────────────────────────────────────────
 */
import { FRAME_HEADER_BYTES, FRAME_MAGIC, FRAME_RECORD_BYTES, FRAME_VERSION, TICK_FIELD_COUNT, frameBytesFor, } from './feed.js';
/**
 * Packs frames into one reused buffer.
 *
 * `capacity` is in RECORDS and is fixed: a flush larger than the buffer is
 * split across frames rather than growing it, because a flusher that
 * reallocates under load does so exactly when the process is busiest.
 */
export class FrameWriter {
    buffer;
    view;
    bytes;
    capacity;
    count = 0;
    constructor(capacity) {
        if (!Number.isInteger(capacity) || capacity <= 0) {
            throw new Error(`FrameWriter capacity must be a positive integer, got ${capacity}`);
        }
        this.capacity = capacity;
        this.buffer = new ArrayBuffer(frameBytesFor(capacity));
        this.view = new DataView(this.buffer);
        this.bytes = new Uint8Array(this.buffer);
    }
    /** Start a frame. Does not clear the buffer — records overwrite in place. */
    begin() {
        this.count = 0;
    }
    get recordCount() {
        return this.count;
    }
    get isFull() {
        return this.count >= this.capacity;
    }
    /**
     * Append one instrument's current values.
     *
     * `values` is the store's flat field array and `offset` is the slot's base
     * index into it, so a record is copied straight out of the store with no
     * intermediate object. Returns false when the frame is full.
     */
    add(token, values, offset) {
        if (this.count >= this.capacity)
            return false;
        let at = FRAME_HEADER_BYTES + this.count * FRAME_RECORD_BYTES;
        this.view.setInt32(at, token, true);
        at += 4;
        for (let f = 0; f < TICK_FIELD_COUNT; f += 1) {
            this.view.setFloat64(at, values[offset + f], true);
            at += 8;
        }
        this.count += 1;
        return true;
    }
    /**
     * Finish the frame and return a view of exactly its bytes.
     *
     * A SUBARRAY of the shared buffer, not a copy: the caller must send it
     * before the next `begin()`. Every caller does — the flusher packs and
     * publishes in one synchronous step — and copying here would allocate per
     * flush, which is the one thing this class exists to avoid.
     */
    end(shard, flushedAt) {
        this.view.setUint16(0, FRAME_MAGIC, true);
        this.view.setUint8(2, FRAME_VERSION);
        this.view.setUint8(3, shard & 0xff);
        this.view.setUint32(4, this.count, true);
        this.view.setFloat64(8, flushedAt, true);
        return this.bytes.subarray(0, frameBytesFor(this.count));
    }
}
/** Read a frame's header without walking its records. */
export function readFrameHeader(data) {
    if (data.byteLength < FRAME_HEADER_BYTES)
        return 'TRUNCATED';
    const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
    if (view.getUint16(0, true) !== FRAME_MAGIC)
        return 'BAD_MAGIC';
    const version = view.getUint8(2);
    /*
     * A browser tab open across a deploy is handed frames by the new engine.
     * Refusing a version we do not know is the difference between a stale tab
     * going quiet and a stale tab decoding garbage into prices.
     */
    if (version !== FRAME_VERSION)
        return 'VERSION_MISMATCH';
    const count = view.getUint32(4, true);
    if (data.byteLength < frameBytesFor(count))
        return 'TRUNCATED';
    return { shard: view.getUint8(3), count, flushedAt: view.getFloat64(8, true), version };
}
/**
 * Walk a frame, calling `apply` once per record.
 *
 * `apply` receives the token and a reader for its fields, so the caller writes
 * directly into its own storage. Nothing is allocated per record.
 *
 * Returns the header on success, or the reason it could not be read.
 */
export function readFrame(data, apply) {
    const header = readFrameHeader(data);
    if (typeof header === 'string')
        return header;
    const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
    let base = FRAME_HEADER_BYTES;
    // One closure for the whole frame, not one per record. `base` is captured by
    // reference, so it follows the loop.
    const field = (index) => view.getFloat64(base + 4 + index * 8, true);
    for (let i = 0; i < header.count; i += 1) {
        apply(view.getInt32(base, true), field);
        base += FRAME_RECORD_BYTES;
    }
    return header;
}
export const isFrameError = (v) => typeof v === 'string';
//# sourceMappingURL=frameCodec.js.map